// External
import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { Box, Flex } from 'rebass';
import { string } from 'prop-types';
import { CSVLink } from 'react-csv';
import { TextStyled, ButtonStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import ActionItemList from '../../common/ActionItemList';
import Spinner from '../../../components/spinner/Spinner';

import { fetchAllCatalogTypeDepartmentAccountIds } from '../actions';
import { fetchAllLinkedEntities } from '../../productContracts/LinkedContractsAndAccounts/actions';

import { getLinkedAccountsColumns } from '../../productContracts/LinkedContractsAndAccounts/linkedAccountsColumns';

import useModal from '../../../hooks/useModal';

const LinkedAccounts = ({ catalogTypeId }) => {
  const {
    totalResources,
    sortQueryParams,
    filterQueryParams,
    handleClearSortAndFilters,
    tableInstance: { selectedRowIds = {}, selectedFlatRows, isAllRowsSelected }
  } = useContext(TableServerSideContext);

  const [linkedAccountIds, setLinkedAccountIds] = useState(null);
  const [isFetchingAccountIds, setIsFetchingAccountIds] = useState(false);

  const getLinkedAccountIds = async () => {
    const accountIds = await fetchAllCatalogTypeDepartmentAccountIds(catalogTypeId);
    setLinkedAccountIds(accountIds);
    setIsFetchingAccountIds(false);
  };

  useEffect(() => {
    if (!linkedAccountIds) {
      setIsFetchingAccountIds(true);
      getLinkedAccountIds();
    }
  }, []);

  const memoizedColumns = useMemo(() => getLinkedAccountsColumns(), []);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();

  const csvLinkRef = useRef(null);

  const [allAccounts, setAllAccounts] = useState([]);

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllAccounts = async () => {
    const filterQueryParamsWithId = `&filter=id(${linkedAccountIds})${filterQueryParams}`;

    const fetchedAccounts = await fetchAllLinkedEntities({
      entity: 'accounts',
      totalResources,
      sortQueryParams,
      filterQueryParams: filterQueryParamsWithId
    });

    return setAllAccounts(fetchedAccounts);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllAccounts(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected account/s?',
        title: 'Confirm Download'
      }
    }
  };

  const csvLinkData = (isAllRowsSelected ? allAccounts : selectedFlatRows?.map(row => row.original))?.map(
    ({ number, name }) => ({ 'Account Number': number, 'Account Name': name })
  );

  return (
    <>
      {isFetchingAccountIds ? (
        <Box alignItems='center' height='100%'>
          <Spinner large />
        </Box>
      ) : (
        <Box pt='15px'>
          <Flex alignItems='center'>
            <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
              {`${totalResources}`} results
            </TextStyled>

            <ButtonStyled
              appearance='text'
              background='light'
              ariaLabel='Clear All Filters'
              type='button'
              onClick={handleClearSortAndFilters}
              style={{ marginRight: '10px' }}
            >
              Clear All Filters
            </ButtonStyled>

            <ActionItemList
              selected={selectedRowIds}
              actionItems={actionItems}
              isModalOpen={isActionItemModalOpen}
              openModal={openActionItemModal}
              closeModal={closeActionItemModal}
            />

            <CSVLink data={csvLinkData || []} filename='LinkedAccounts.csv' ref={csvLinkRef} target='_blank' />
          </Flex>
          <TableServerSide
            columns={memoizedColumns}
            url={`v1/accounts?filter=id(${linkedAccountIds})`}
            includeRowSelect
            fullWidth
            fullHeight
          />
        </Box>
      )}
    </>
  );
};

LinkedAccounts.propTypes = {
  catalogTypeId: string.isRequired
};

export default LinkedAccounts;
